import React, { useState, useRef } from "react";
import {
    Box,
    Button,
    TextField,
    IconButton,
    Typography,
    Paper,
    Alert,
    Collapse,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ClearIcon from "@mui/icons-material/Clear";
import UploadIcon from "@mui/icons-material/Upload";
import ConfirmDialog from "../../Common/ConfirmDialog";

export default function CalibrationData({ rows, setRows }) {

    const [textArea, setTextArea] = useState("");
    const [errors, setErrors] = useState([]);
    const [showError, setShowError] = useState(true);
    const fileInputRef = useRef(null);
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const addRow = () => {
        setRows((prev) => [...prev, { conc: "", resp: "" }]);
    };
    const createEmptyRows = (count = 5) =>
        Array.from({ length: count }, () => ({
            conc: "",
            resp: "",
        }));
    const safeRows = rows?.length ? rows : createEmptyRows(5);
    const removeRow = (index) => {
        setRows((prev) => {
            if (prev.length <= 1) {
                return [{ conc: "", resp: "" }];
            }

            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    };
    const handleRowChange = (index, field, value) => {
        const updated = [...rows];
        updated[index] = {
            ...updated[index],
            [field]: value
        };

        setRows(updated);
    };
    const resetGrid = () => {
        setRows(createEmptyRows(5));
    };
    const clearTextarea = () => {
        setTextArea("");
        setErrors([]);
        setShowError(false);

        setRows(createEmptyRows(5)); 
    };

    // --- Parse logic: group responses by concentration
    const handleParseText = (content) => {
        setErrors([]);
        setShowError(true);

        const text = content !== undefined ? content : textArea;

        if (!text.trim()) {
            setErrors(["Values are empty. Please enter or upload data."]);
            return;
        }

        const lines = text
            .split("\n")
            .filter((l) => l.trim() !== "");

        const groupMap = {};
        const tempErrors = [];

        lines.forEach((line, index) => {
            if (/Concentration|Response/i.test(line)) return;

            // Split by tab, comma, or spaces
            const cols = line.trim().split(/[\t ,]+/).map((c) => c.trim());

            if (cols.length < 2) {
                tempErrors.push(`Line ${index + 1} is invalid: "${line}"`);
                return;
            }

            const conc = cols[0];
            const resp = cols[1];

            if (isNaN(parseFloat(conc)) || isNaN(parseFloat(resp))) {
                tempErrors.push(
                    `Line ${index + 1} contains non-numeric value: "${line}"`
                );
                return;
            }

            if (groupMap[conc]) {
                groupMap[conc].push(resp);
            } else {
                groupMap[conc] = [resp];
            }
        });

        setErrors(tempErrors);

        const parsedRows = Object.entries(groupMap).map(([conc, resps]) => ({
            conc: parseFloat(conc),
            resp: resps.join(", "),
        }));

        if (parsedRows.length > 0) {
            setRows(parsedRows);
        } else if (tempErrors.length === 0) {
            setRows(createEmptyRows(5));
        }
        else if (parsedRows.length === 0 && tempErrors.length === 0)
            setRows([{ conc: "", resp: "" }]);
    };
    const formatTabularText = (text) => {

        const lines = text
            .split(/\r?\n/)
            .filter(line => line.trim());

        if (!lines.length) return "";

        // Split CSV columns
        const rows = lines.map(line =>
            line.split(",").map(v => v.trim())
        );

        // Find max width for each column
        const colWidths = [];

        rows.forEach(row => {

            row.forEach((cell, i) => {

                colWidths[i] = Math.max(
                    colWidths[i] || 0,
                    cell.length
                );
            });
        });

        // Build aligned text
        return rows
            .map(row =>
                row
                    .map((cell, i) =>
                        cell.padEnd(colWidths[i] + 4)
                    )
                    .join("")
            )
            .join("\n");
    };
    // --- Handle file upload
    const handleFile = (file) => {
        if (!file) return; // safety check

        const reader = new FileReader();

        reader.onload = (e) => {
            let content;
            const ext = file.name.split(".").pop().toLowerCase();

            if (ext === "csv" || ext === "txt") {
                content = e.target.result;
            } else if (ext === "xlsx" || ext === "xls") {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                content = XLSX.utils.sheet_to_csv(sheet);
            } else {
                alert("Unsupported file type. Please use TXT, CSV or XLSX.");
                return;
            }
            const formattedText =
                formatTabularText(content);

            setTextArea(formattedText);
           
            // Reset grid and errors
            setRows([{ conc: "", resp: "" }]);
            setErrors([]);
            setShowError(true);

            // Parse using file content directly
            handleParseText(content);
        };

        if (file.name.endsWith("xlsx") || file.name.endsWith("xls")) {
            reader.readAsArrayBuffer(file);
        } else {
            reader.readAsText(file);
        }
    };

    const handleUploadClick = () => fileInputRef.current.click();
    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleTextAreaChange = (e) => {
        setTextArea(e.target.value);
        setRows(createEmptyRows(5));
        setErrors([]);
        setShowError(false);
    };
    const isDefaultEmptyRows = (rows) => {
        if (!rows || rows.length === 0) return true;

        return rows.every(
            (r) =>
                (!r.conc || r.conc === "") &&
                (!r.resp || r.resp === "")
        );
    };
    return (
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                border: "none",
                overflow: "hidden",

            }}
        >
            {/* Main Responsive Layout */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: {
                        xs: "column",
                        md: "row",
                    },
                    gap: 2,
                    width: "100%",
                    alignItems: "stretch",
                }}
            >
                {/* LEFT SIDE - INPUT SECTION */}
                <Box
                    sx={{
                        width: {
                            xs: "100%",
                            md: "42%",
                        },
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,

                        height: {
                            xs: "auto",
                            lg: "45vh",
                        },

                        minHeight: {
                            xs: 250,
                            sm: 290,
                            md: 320,
                        },
                    }}
                >
                    {/* Textarea with upload overlay */}
                    <Box
                        sx={{
                            borderColor: "grey.300",
                            borderRadius: 1,
                            position: "relative",
                            p: 1,
                            border: dragActive
                                ? "1px dashed #1976d2"
                                : "1px solid #d0d0d0",

                            backgroundColor: dragActive
                                ? "rgba(25,118,210,0.05)"
                                : "transparent",

                            display: "flex",
                            flexDirection: "column",

                            flex: 1,
                            minHeight: 0,
                        }}

                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragActive(true);
                        }}
                        onDragLeave={() => setDragActive(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragActive(false);
                            handleDrop(e);
                        }}
                    >
                        <TextField
                            name="TXBParse"
                            multiline
                            fullWidth
                            minRows={5}
                            maxRows={20}
                            value={textArea}
                            onChange={handleTextAreaChange}
                            variant="outlined"
                            placeholder={
                                `Example:
Concentration\tResponse
0.13\t\t-0.019737
0.13\t\t-0.019737`
                            }
                            InputProps={{
                                sx: {
                                    fontFamily: "Consolas, Monaco, monospace",
                                    fontSize: "14px",

                                    "& textarea": {
                                        whiteSpace: "pre",
                                        tabSize: 8,
                                        overflowX: "auto",
                                        overflowY: "auto",
                                        wordBreak: "normal",
                                        lineHeight: 1.6,
                                    },
                                },
                            }}
                            inputProps={{
                                wrap: "off"
                            }}
                            sx={{
                                flex: 1,

                                "& .MuiOutlinedInput-root": {
                                    height: "100%",
                                    alignItems: "flex-start",
                                    p: 1,
                                },

                                "& textarea": {
                                    height: "100% !important",
                                    overflowY: "auto !important",
                                },

                                "& textarea::placeholder": {
                                    fontFamily: "Consolas, Monaco, monospace",
                                    whiteSpace: "pre",
                                    opacity: 0.8,
                                    color: "#777",
                                },
                            }}
                        />
                 
                        {!textArea && (
                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    align="center"
                                    sx={{
                                        position: "absolute",
                                        top: "38%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        zIndex: 2,
                                        width: "90%",
                                        fontSize: {
                                            xs: "0.9rem",
                                            sm: "1rem",
                                        },
                                    }}
                                >
                                    Drag & drop, paste or enter values.
                                </Typography>

                                <Button
                                    variant="contained"
                                    size="medium"
                                    startIcon={<UploadFileIcon />}
                                    onClick={handleUploadClick}
                                    sx={{
                                        position: "absolute",
                                        top: "52%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        zIndex: 2,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Upload File
                                </Button>
                            </Box>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls,.txt"
                            style={{ display: "none" }}
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    handleFile(e.target.files[0]);

                                    // allow same file upload again
                                    e.target.value = "";
                                }
                            }}
                        />
                    </Box>

                    {/* Error alert */}
                    {errors.length > 0 && (
                        <Collapse in={showError} sx={{ mt: 1 }}>
                            <Alert
                                severity="error"
                                action={
                                    <IconButton
                                        aria-label="close"
                                        color="inherit"
                                        size="small"
                                        onClick={() => setShowError(false)}
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                }
                                sx={{ p: 0 }}
                            >
                                <Box
                                    sx={{
                                        maxHeight: 150,
                                        overflowY: "auto",
                                        padding: 1,
                                    }}
                                >
                                    {errors.map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                                </Box>
                            </Alert>
                        </Collapse>
                    )}

                  
                </Box>

                {/* RIGHT SIDE - GRID SECTION */}
                <Box
                    sx={{
                        width: {
                            xs: "100%",
                            md: "58%",
                        },
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                    }}
                >
                    {/* Grid */}
                    <Box
                        border={1}
                        borderColor="grey.300"
                        borderRadius={1}
                        sx={{
                            p: 1,
                            overflowY: "auto",

                            height: {
                                xs: "auto",
                                lg: "45vh",
                            },

                            minHeight: {
                                xs: 250,
                                sm: 290,
                                md: 320,
                            },
                        }}
                    >
                        {safeRows.map((row, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    flexDirection: {
                                        xs: "column",
                                        sm: "row",
                                    },
                                    gap: 1,
                                    alignItems: {
                                        xs: "stretch",
                                        sm: "center",
                                    },
                                    padding: 1,
                                   
                                }}
                            >
                                <TextField
                                    label="Concentration"
                                    size="small"
                                    value={row.conc}
                                    onChange={(e) =>
                                        handleRowChange(
                                            index,
                                            "conc",
                                            e.target.value
                                        )
                                    }
                                    fullWidth
                                />

                                <TextField
                                    label="Response"
                                    size="small"
                                    value={row.resp}
                                    onChange={(e) =>
                                        handleRowChange(
                                            index,
                                            "resp",
                                            e.target.value
                                        )
                                    }
                                    fullWidth
                                />

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: {
                                            xs: "flex-end",
                                            sm: "center",
                                        },
                                        alignItems: "center",
                                    }}
                                >
                                    <IconButton
                                        onClick={() => removeRow(index)}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                       
                    </Box>

                  
                </Box>
               
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                {/* LEFT BUTTONS */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        onClick={() => handleParseText()}
                        disabled={!textArea?.trim()} >
                        Parse Values
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<ClearIcon />}
                        onClick={clearTextarea}
                        disabled={!textArea?.trim()} >
                        Clear Values
                    </Button>
                </Box>

                {/* RIGHT BUTTONS */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={addRow}
                    >
                        Add Calibration
                    </Button>

                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={() => setOpenResetDialog(true)}
                        disabled={isDefaultEmptyRows(rows)}
                    >
                        Reset Calibration
                    </Button>
                </Box>
            </Box>
            <ConfirmDialog
                open={openResetDialog}
                title="Calibration Reset"
                message="Are you sure you want to clear the calibration data?"
                onCancel={() => setOpenResetDialog(false)}
                onConfirm={() => {
                    resetGrid();
                    setOpenResetDialog(false);
                }}
            />
        </Paper>
    );
}